import { useEffect, useRef, useState } from "react";
import { ResponsiveImage } from "@/components/common/ResponsiveImage";

const DESKTOP_BASE = "/assets/01-home-hero-desktop";
const MOBILE_BASE = "/assets/10-home-hero-mobile";

// Four-stage motion frames, loaded only after the poster + essential content.
const STAGES = [
  { label: "01 · Problem", caption: "An unresolved technical challenge inside a live programme.", img: "/assets/02-what-we-solve-hero.webp" },
  { label: "02 · Assembly", caption: "Relevant specialists across distributed workplaces.", img: "/assets/04-network-hero.webp" },
  { label: "03 · Engineering", caption: "Prototype integration and structured testing.", img: "/assets/05-projects-hero.webp" },
  { label: "04 · Validation", caption: "A functioning system and a measured, bounded result.", img: "/assets/07-about-hero.webp" },
];

function useMediaQuery(query) {
  const [match, setMatch] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const on = () => setMatch(m.matches);
    on();
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, [query]);
  return match;
}

export function HeroMotion() {
  const isMobile = useMediaQuery("(max-width: 1023px)");
  const reduced = useMediaQuery("(prefers-reduced-motion: reduce)");
  const motionEnabled = !isMobile && !reduced;

  const [framesReady, setFramesReady] = useState(false); // deferred load gate
  const [i, setI] = useState(0);
  const [inView, setInView] = useState(true);
  const [failed, setFailed] = useState({});
  const ref = useRef(null);

  const poster = isMobile ? MOBILE_BASE : DESKTOP_BASE;

  // Defer motion-frame loading until after first paint / idle.
  useEffect(() => {
    if (!motionEnabled) return;
    const cb = () => setFramesReady(true);
    const id = window.requestIdleCallback ? window.requestIdleCallback(cb, { timeout: 2000 }) : setTimeout(cb, 1200);
    return () => (window.cancelIdleCallback ? window.cancelIdleCallback(id) : clearTimeout(id));
  }, [motionEnabled]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.25 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!motionEnabled || !framesReady) return;
    const active = inView && !document.hidden;
    if (!active) return;
    const t = setInterval(() => setI((p) => (p + 1) % STAGES.length), 3500);
    return () => clearInterval(t);
  }, [motionEnabled, framesReady, inView]);

  const current = i;
  const showStage = motionEnabled && framesReady;

  return (
    <div ref={ref} className="relative h-full w-full overflow-hidden bg-[var(--navy)]" data-testid="hero-motion">
      {/* Poster: LCP image and no-motion fallback. Always visible beneath frames. */}
      <ResponsiveImage
        key={poster}
        base={poster}
        mobile={isMobile}
        eager
        priority
        width={isMobile ? 900 : 1600}
        height={isMobile ? 1599 : 900}
        sizes="(max-width: 1023px) 100vw, 56vw"
        alt="Scientists and engineers converging across a research environment."
        className="absolute inset-0 h-full w-full object-cover"
      />
      <span data-testid="hero-poster" data-src={poster} className="hidden" />
      {showStage &&
        STAGES.map((s, idx) => (
          !failed[idx] && (
            <img
              key={s.img}
              src={s.img}
              alt=""
              aria-hidden="true"
              width="1600"
              height="900"
              loading="lazy"
              onError={() => setFailed((f) => ({ ...f, [idx]: true }))}
              className={`motion-frame absolute inset-0 h-full w-full object-cover ${idx === current ? "active" : ""}`}
            />
          )
        ))}
      <div className="absolute inset-0 bg-gradient-to-t from-[var(--navy)]/70 via-transparent to-transparent" />
      {showStage && (
        <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.16em] text-[var(--cyan)]">{STAGES[current].label}</p>
            <p className="mt-1 max-w-sm text-sm text-white/90">{STAGES[current].caption}</p>
          </div>
          <div className="flex gap-1.5" role="tablist" aria-label="Motion stage">
            {STAGES.map((s, idx) => (
              <button
                key={s.label}
                aria-label={`Show stage ${s.label}`}
                data-testid={`motion-dot-${idx}`}
                onClick={() => setI(idx)}
                className={`h-1.5 rounded-full transition-all ${idx === current ? "w-6 bg-[var(--amber)]" : "w-1.5 bg-white/40"}`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
